import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Code2, Eye, KeyRound, Loader2, LogOut, Plus, Settings, ShieldAlert, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { BrandMark } from "@/components/AuthShell";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { EmbedCode } from "@/components/EmbedCode";
import { useAuth } from "@/context/AuthContext";
import { useCustomerConfig } from "@/components/admin/useCustomerConfig";
import { FeeTableCard } from "@/components/admin/FeeTableCard";
import { AppearancePanel } from "@/components/admin/AppearancePanel";
import { NoteEditor } from "@/components/admin/NoteEditor";
import { LogoPanel } from "@/components/admin/LogoPanel";
import { LivePreview } from "@/components/admin/LivePreview";
import { EditableText } from "@/components/admin/EditableCell";
import { newId } from "@/lib/format";
import { PoweredBy } from "@/components/PoweredBy";
import { TeamPanel } from "@/components/admin/TeamPanel";
import { VesselTypes } from "@/components/admin/VesselTypes";
import { NewTableDialog } from "@/components/admin/NewTableDialog";
import { CustomCalcs } from "@/components/admin/CustomCalcs";
import { AuditLog } from "@/components/super/AuditLog";
import { PaxPanel } from "@/components/pax/PaxPanel";
import { AnlopPanel } from "@/components/anlop/AnlopPanel";
import { PriceSchedule, ScheduleBanner } from "@/components/admin/PriceSchedule";

const tabsFor = (c) => [["kalkulator", "Kalkulator"], ...(c.pax_enabled !== false ? [["passasjer", "Skjema for passasjervederlag"]] : []),
  ...(c.anlop_enabled ? [["anlop", c.anlop?.title || "Anløpsliste"]] : [])];

const TabBar = ({ tab, setTab, tabs, settings, setSettings }) => (
  <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-6 flex items-center justify-between gap-3">
    <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1 gap-1 max-w-full overflow-x-auto min-w-0" role="tablist">
      {tabs.map(([k, l]) => (
        <button key={k} type="button" role="tab" aria-selected={tab === k && !settings} onClick={() => setTab(k)} data-testid={`tab-${k}`}
          className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${tab === k ? (settings ? "bg-slate-200 text-slate-900" : "bg-[#0A192F] text-white") : "text-slate-600 hover:bg-slate-100"}`}>{l}</button>
      ))}
    </div>
    <button type="button" onClick={() => setSettings(!settings)} aria-pressed={settings} data-testid="settings-button" title="Innstillinger"
      className={`shrink-0 inline-flex items-center gap-2 rounded-lg border px-3 sm:px-4 py-2.5 text-sm font-medium transition-colors ${settings ? "bg-[#0A192F] text-white border-[#0A192F]" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-100"}`}>
      <Settings className="w-4 h-4" /><span className="hidden sm:inline">Innstillinger</span>
    </button>
  </div>
);

const SettingsHead = ({ label, onBack }) => (
  <div className="flex flex-wrap items-end justify-between gap-3 border-b border-slate-200 pb-4" data-testid="settings-view">
    <div>
      <h1 className="font-display text-2xl font-semibold text-slate-900">Innstillinger for {label}</h1>
      <p className="text-sm text-slate-500 mt-1">Ting du vanligvis bare setter opp én gang. Alt lagres automatisk.</p>
    </div>
    <Button variant="outline" size="sm" onClick={onBack} data-testid="settings-back"><ArrowLeft className="w-4 h-4 mr-1.5" />Tilbake</Button>
  </div>
);

const SaveBadge = ({ state }) => {
  if (state === "saving") return <span className="inline-flex items-center gap-1.5 text-xs text-slate-500" data-testid="save-status"><Loader2 className="w-3.5 h-3.5 animate-spin" />Lagrer…</span>;
  if (state === "error") return <span className="text-xs text-red-600" data-testid="save-status">Ikke lagret</span>;
  return <span className="inline-flex items-center gap-1.5 text-xs text-emerald-700" data-testid="save-status"><CheckCircle2 className="w-3.5 h-3.5" />Alt er lagret</span>;
};

const Section = ({ id, title, hint, children, action }) => (
  <section id={id} className="scroll-mt-28">
    <div className="flex items-end justify-between gap-3 mb-4">
      <div>
        <h2 className="font-display text-xl font-semibold text-slate-900">{title}</h2>
        {hint && <p className="text-sm text-slate-500 mt-1">{hint}</p>}
      </div>
      {action}
    </div>
    {children}
  </section>
);

const NAV_MAIN = [["tabeller", "Avgifter"], ["endringslogg", "Endringslogg"], ["preview", "Forhåndsvisning"]];
const NAV_SETTINGS = [["prisplan", "Tidsstyrte priser"], ["fartoy", "Fartøytyper"], ["tekst", "Tekstfelt"], ["utseende", "Utseende"], ["logo", "Logo"], ["embed", "Embed-kode"], ["brukere", "Brukere"], ["preview", "Forhåndsvisning"]];
const SETTINGS_IDS = ["prisplan", "fartoy", "tekst", "utseende", "logo", "embed", "brukere"];

const newTable = () => ({
  id: newId(), name: "Ny avgiftstabell", min_price: 0, per_day: false, method: "progressive",
  tiers: [[0, 300], [300, 600], [600, 2000], [2000, 4000], [4000, 10000], [10000, null]].map(([a, b]) => ({ from_bt: a, to_bt: b, rate: 0 })),
});

function Header({ c, isSuper, saveState, save, user, onLogout, onPw, onUsers }) {
  return (
    <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-200">
      {isSuper && (
        <div className="bg-amber-400 text-amber-950 text-sm px-4 py-1.5 flex items-center justify-between gap-3" data-testid="impersonation-banner">
          <span className="flex items-center gap-2 min-w-0"><ShieldAlert className="w-4 h-4 shrink-0" /><span className="truncate">Du emulerer kundeadmin for <strong>{c.name}</strong>. Endringer logges.</span></span>
          <button type="button" onClick={() => { window.close(); setTimeout(() => { window.location.href = "/superadmin"; }, 150); }} className="font-medium underline whitespace-nowrap" data-testid="exit-impersonation-link">Avslutt</button>
        </div>
      )}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {c.logo ? <img src={c.logo} alt="" className="h-9 max-w-[120px] object-contain" data-testid="header-logo" /> : <div className="hidden sm:block"><BrandMark /></div>}
          <div className="min-w-0 sm:border-l sm:border-slate-200 sm:pl-3">
            {isSuper ? (
              <EditableText value={c.name} onCommit={(v) => save("name", v, 0)} testId="customer-name" className="font-display font-semibold text-slate-900 truncate" maxLength={100} />
            ) : (
              <div className="font-display font-semibold text-slate-900 truncate" data-testid="customer-name">{c.name}</div>
            )}
            <div className="text-xs text-slate-500">Kundeadmin</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <SaveBadge state={saveState} />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="rounded-full w-9 h-9 p-0 font-semibold" data-testid="user-menu-button">
                {(user.name || user.email).slice(0, 1).toUpperCase()}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel className="font-normal text-xs text-slate-500">{user.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onUsers} data-testid="menu-users"><Users className="w-4 h-4 mr-2" />Brukere</DropdownMenuItem>
              <DropdownMenuItem onClick={onPw} data-testid="menu-change-password"><KeyRound className="w-4 h-4 mr-2" />Bytt passord</DropdownMenuItem>
              <DropdownMenuItem onClick={onLogout} data-testid="menu-logout"><LogOut className="w-4 h-4 mr-2" />Logg ut</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}

export default function CustomerAdmin() {
  const { cid } = useParams();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const { c, error, save, saveState, setLocal } = useCustomerConfig(cid);
  const [pwOpen, setPwOpen] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [tab, setTabState] = useState(() => {
    const f = new URLSearchParams(window.location.search).get("fane");
    return f === "passasjer" || f === "anlop" ? f : "kalkulator";
  });
  const setTab = (t) => {
    setTabState(t);
    setSettings(false);
    window.history.replaceState(null, "", t === "kalkulator" ? window.location.pathname : `?fane=${t}`);
  };
  const [settings, setSettings] = useState(() => SETTINGS_IDS.includes(window.location.hash.slice(1)));
  const goSettings = (id) => {
    setTabState("kalkulator");
    window.history.replaceState(null, "", window.location.pathname);
    setSettings(true);
    setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
  };
  useEffect(() => {
    const onHash = () => {
      const id = window.location.hash.slice(1);
      if (SETTINGS_IDS.includes(id)) goSettings(id);
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  const toggleSettings = (v) => {
    setSettings(v);
    if (!v && window.location.hash) window.history.replaceState(null, "", window.location.pathname + window.location.search);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const isSuper = user.role === "super_admin";

  const config = useMemo(
    () => (c ? { settings: c.settings, tables: c.tables, note_html: c.note_html, note_visible: c.note_visible,
      general_enabled: c.general_enabled, cruise_enabled: c.cruise_enabled, cruise_tables: c.cruise_tables, fishing_enabled: c.fishing_enabled, fishing: c.fishing, leisure_enabled: c.leisure_enabled, leisure: c.leisure, extras: c.extras, other_enabled: c.other_enabled, other_groups: c.other_groups, custom_calcs: c.custom_calcs } : null),
    [c]
  );

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6 text-center" data-testid="customer-admin-error">
        <p className="text-slate-700">{error}</p>
        <Button asChild variant="outline"><Link to={isSuper ? "/superadmin" : "/login"}><ArrowLeft className="w-4 h-4 mr-2" />Tilbake</Link></Button>
      </div>
    );
  }
  if (!c) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-sky-700" /></div>;

  const onLogout = async () => {
    await logout();
    nav(isSuper ? "/superadmin/login" : "/login");
  };
  const updateTable = (i, t) => save("tables", c.tables.map((x, j) => (j === i ? t : x)));
  const tabs = tabsFor(c);
  const curTab = tabs.some(([k]) => k === tab) ? tab : "kalkulator";
  const tabLabel = (tabs.find(([k]) => k === curTab) || [])[1];

  return (
    <div className="min-h-screen" data-testid="customer-admin-page">
      <Header c={c} isSuper={isSuper} saveState={saveState} save={save} user={user} onLogout={onLogout} onPw={() => setPwOpen(true)} onUsers={() => goSettings("brukere")} />
      {c.price_schedule && <ScheduleBanner schedule={c.price_schedule} />}
      <TabBar tab={curTab} setTab={setTab} tabs={tabs} settings={settings} setSettings={toggleSettings} />
      {curTab === "anlop" ? (
        <main className="max-w-7xl mx-auto px-4 sm:px-6 pt-8 pb-16 space-y-8">
          {settings && <SettingsHead label={tabLabel} onBack={() => toggleSettings(false)} />}
          <AnlopPanel cid={cid} setLocal={setLocal} settings={settings} onSettings={() => toggleSettings(true)} />
        </main>
      ) : curTab === "passasjer" ? (
        <main className="max-w-7xl mx-auto px-4 sm:px-6 pt-8 pb-16 space-y-8">
          {settings && <SettingsHead label={tabLabel} onBack={() => toggleSettings(false)} />}
          <PaxPanel c={c} cid={cid} save={save} settings={settings} onSettings={() => toggleSettings(true)} />
        </main>
      ) : (<>
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 pt-5 flex gap-2 overflow-x-auto lg:hidden">
        {(settings ? NAV_SETTINGS : NAV_MAIN).map(([id, label]) => (
          <a key={id} href={`#${id}`} className="px-3 py-1.5 rounded-full bg-white border border-slate-200 text-sm whitespace-nowrap text-slate-700 hover:border-sky-400" data-testid={`nav-${id}`}>{label}</a>
        ))}
      </nav>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 pt-8 pb-24 lg:py-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7 space-y-12 min-w-0">
          {settings ? (<>
          <SettingsHead label="Kalkulator" onBack={() => toggleSettings(false)} />
          <Section id="prisplan" title="Tidsstyrte priser" hint="Forbered nye priser nå, og velg når de skal vises i kalkulatoren på nettsiden.">
            <PriceSchedule cid={cid} schedule={c.price_schedule} setLocal={setLocal} />
          </Section>
          <Section id="fartoy" title="Fartøytyper, ISPS og avfallsgebyr" hint="Har havnen egne priser for cruiseskip eller fiskefartøy? Slå dem på her. Prisene for dem finner du under Kalkulator.">
            <VesselTypes c={c} save={save} newTable={newTable} part="switches" />
          </Section>
          <Section id="tekst" title="Tekstfelt under kalkulatoren">
            <NoteEditor key={cid} html={c.note_html} visible={c.note_visible} onHtml={(h) => save("note_html", h, 800)} onVisible={(v) => save("note_visible", v, 0)} />
          </Section>
          <Section id="utseende" title="Farger og font" hint="Tilpass kalkulatoren til nettsidens profil.">
            <AppearancePanel settings={c.settings} onChange={(s) => save("settings", s, 400)} />
          </Section>
          <Section id="logo" title="Logo">
            <LogoPanel cid={cid} logo={c.logo} onLogo={(l) => setLocal("logo", l)} />
          </Section>
          <Section id="embed" title="Embed-kode" hint="Lim inn koden på nettsiden der kalkulatoren skal vises.">
            <div className="bg-white border border-slate-200 rounded-lg p-4 sm:p-5"><EmbedCode cid={cid} /></div>
          </Section>
          <Section id="brukere" title="Brukere" hint="Gi kolleger egen innlogging til kalkulatoren. Alle brukere kan redigere tabeller og innstillinger.">
            <TeamPanel cid={cid} meId={user?.id} />
          </Section>
          </>) : (<>
          <Section id="tabeller" title="Avgiftstabeller" hint={c.price_schedule ? `Klikk på et tall for å endre det. Endringer lagres automatisk, men vises først på nettsiden når tidsstyrte priser trer i kraft.` : "Klikk på et tall for å endre det. Alt lagres automatisk og vises på nettsiden med en gang."}
            action={<Button size="sm" onClick={() => setNewOpen(true)} data-testid="add-table-button"><Plus className="w-4 h-4 mr-1" />Ny tabell</Button>}>
            <div className="space-y-5">
              {c.tables.map((t, i) => (
                <FeeTableCard key={t.id} table={t} onChange={(nt) => updateTable(i, nt)} canDelete={c.tables.length > 1}
                  onDelete={() => save("tables", c.tables.filter((_, j) => j !== i), 0)} />
              ))}
              <CustomCalcs c={c} save={save} />
              <VesselTypes c={c} save={save} newTable={newTable} part="tables" />
            </div>
            <NewTableDialog open={newOpen} onOpenChange={setNewOpen}
              onCreate={(target, v) => (target === "own" ? save("custom_calcs", [...(c.custom_calcs || []), v], 0) : save("tables", [...c.tables, v], 0))} />
          </Section>
          <section id="endringslogg" className="scroll-mt-28">
            <AuditLog customerId={cid} refreshKey={saveState} onReverted={(r) => r?.field && setLocal(r.field, r.value)} />
          </section>
          </>)}
        </div>
        <aside id="preview" className="lg:col-span-5 scroll-mt-28 min-w-0">
          <div className="lg:sticky lg:top-24 space-y-3">
            <div className="flex items-center gap-2 text-sm text-slate-500"><Eye className="w-4 h-4" />{c.price_schedule ? "Forhåndsvisning med de nye prisene (nettsiden viser dagens priser)" : "Slik ser kalkulatoren ut på nettsiden"}</div>
            <LivePreview cid={cid} config={config} />
            <button type="button" onClick={() => goSettings("embed")} className="hidden lg:inline-flex items-center gap-1.5 text-sm text-sky-700 hover:underline" data-testid="preview-embed-link"><Code2 className="w-4 h-4" />Hent embed-kode</button>
          </div>
        </aside>
      </main>
      </>)}
      <footer className="max-w-7xl mx-auto px-4 sm:px-6 pb-24 lg:pb-8 -mt-12 lg:mt-0">
        <PoweredBy className="border-t border-slate-200 pt-5" />
      </footer>
      {tab === "kalkulator" && <a href="#preview" className="lg:hidden fixed bottom-4 right-4 z-20 inline-flex items-center gap-2 rounded-full bg-[#0A192F] text-white px-4 py-2.5 text-sm shadow-lg" data-testid="mobile-preview-fab">
        <Eye className="w-4 h-4" />Forhåndsvis
      </a>}
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
    </div>
  );
}
